import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { FINANCE_NAV } from './nav';

interface RevenueData {
    total_revenue: number; outstanding_invoices: number;
    revenue_by_day: { date: string; total: number }[];
    transaction_success_rate: number;
    transactions_by_status: { name: string; value: number }[];
}

const RevenueOverview: React.FC = () => {
    const { api } = useInternalApi();
    const [data, setData] = useState<RevenueData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Revenue Overview - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('admin-dashboard/dashboards/revenue-billing/').then(res => {
            if (res.data?.status === 'success') setData(res.data.data);
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InternalShell activeDashboard="finance_center" subNav={FINANCE_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />Revenue Overview</h2>
            <p className="text-muted mb-4">Platform-wide revenue and payment gateway performance</p>

            {loading || !data ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Total Revenue</div>
                                <h4>KES {data.total_revenue.toLocaleString()}</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Outstanding Invoices</div>
                                <h4>{data.outstanding_invoices}</h4>
                            </div></div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Gateway Success Rate</div>
                                <h4>{data.transaction_success_rate}%</h4>
                            </div></div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-7 mb-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white"><strong>Revenue - Last 30 Days</strong></div>
                                <div className="card-body p-0">
                                    {data.revenue_by_day.length === 0 ? (
                                        <div className="text-center py-4 text-muted">No revenue recorded in the last 30 days.</div>
                                    ) : (
                                        <table className="table table-sm mb-0"><tbody>
                                            {data.revenue_by_day.map(r => (
                                                <tr key={r.date}><td>{r.date}</td><td className="text-end">KES {r.total.toLocaleString()}</td></tr>
                                            ))}
                                        </tbody></table>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-5 mb-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white"><strong>Transactions by Status</strong></div>
                                <div className="card-body p-0">
                                    {data.transactions_by_status.length === 0 ? (
                                        <div className="text-center py-4 text-muted">No transactions yet.</div>
                                    ) : (
                                        <table className="table table-sm mb-0"><tbody>
                                            {data.transactions_by_status.map(t => (
                                                <tr key={t.name}><td className="text-capitalize">{t.name}</td><td className="text-end"><span className="badge bg-secondary">{t.value}</span></td></tr>
                                            ))}
                                        </tbody></table>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default RevenueOverview;
